import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, LogOut, Loader2, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let adminPw = ''; // Backup password: in-memory only, this tab.
let adminUser = null; // Google-signed-in staff (session cookie is the source of truth).
export const setAdminPw = (v) => { adminPw = v; };
export const setAdminUser = (u) => { adminUser = u; };
export const AUTH_ERROR_KEY = 'ls_admin_auth_error';

export const adminFetch = (path, opts = {}) => {
  const isJson = opts.body && !(opts.body instanceof FormData);
  return fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { ...(adminPw ? { 'X-Admin-Password': adminPw } : {}), ...(isJson ? { 'Content-Type': 'application/json' } : {}), ...(opts.headers || {}) },
  });
};

const startGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + window.location.pathname;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const NAV = [
  { to: '/admin/chats', label: 'Dashboard' },
  { to: '/admin/waitlists/foundations', label: 'Waitlists', match: '/admin/waitlists' },
  { to: '/admin/blog', label: 'Blog' },
  { to: '/admin/sponsor', label: 'Sponsor' },
];

export function AdminNav({ user, onLogout }) {
  const { pathname } = useLocation();
  return (
    <nav className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-nav">
      {NAV.map((n) => {
        const on = pathname.startsWith(n.match || n.to);
        return (
          <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase()}`} className={`rounded-full px-4 py-2 text-sm font-semibold border ${on ? 'bg-splash-green text-white border-splash-green' : 'bg-white text-splash-green border-splash-sand'}`}>{n.label}</Link>
        );
      })}
      <div className="ml-auto flex items-center gap-3">
        {user && (
          <span className="flex items-center gap-2 text-sm text-splash-green" data-testid="admin-user">
            {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />}
            <span className="font-semibold hidden sm:inline">{user.name}</span>
          </span>
        )}
        <button type="button" onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-1 text-sm text-splash-green/70 hover:text-splash-coral"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </nav>
  );
}

function SignInCard({ title, onBackup }) {
  const [authError] = useState(() => {
    const e = sessionStorage.getItem(AUTH_ERROR_KEY) || '';
    sessionStorage.removeItem(AUTH_ERROR_KEY);
    return e;
  });
  const [showBackup, setShowBackup] = useState(false);
  const [input, setInput] = useState('');
  const [err, setErr] = useState('');

  const backupLogin = async (e) => {
    e.preventDefault();
    setErr('');
    const r = await fetch(`${API}/admin/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: input }) });
    if (!r.ok) { setErr(r.status === 429 ? 'Too many attempts. Try again in 15 minutes.' : 'Invalid password'); return; }
    setAdminPw(input);
    onBackup();
  };

  return (
    <main className="min-h-screen bg-splash-cream flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-splash-sand shadow-sm p-8 text-center" data-testid="admin-gate">
        <Lock className="w-8 h-8 text-splash-teal mx-auto mb-3" />
        <h1 className="font-display font-bold text-2xl text-splash-green">{title}</h1>
        <p className="text-sm text-splash-green/70 mt-1">Staff sign in with your @littlesplashswimpro.com Google account.</p>
        {authError && (
          <div className="mt-5 rounded-2xl bg-splash-coral/10 border border-splash-coral/30 p-3 text-sm text-splash-coralDark flex gap-2 text-left" data-testid="admin-auth-error">
            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" /> {authError}
          </div>
        )}
        <button type="button" onClick={startGoogle} data-testid="admin-google-signin" className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full border border-splash-sand bg-white hover:bg-splash-cream text-splash-green py-3 font-semibold btn-transition">
          <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
          {authError ? 'Try another account' : 'Sign in with Google'}
        </button>
        {!showBackup ? (
          <button type="button" onClick={() => setShowBackup(true)} data-testid="admin-backup-toggle" className="mt-6 text-xs text-splash-green/60 hover:text-splash-green underline">Use backup password</button>
        ) : (
          <form onSubmit={backupLogin} className="mt-6 text-left" data-testid="admin-gate-form">
            <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Backup password" className="input" data-testid="admin-gate-password" autoFocus />
            {err && <div className="text-sm text-red-600 mt-2" data-testid="admin-gate-error">{err}</div>}
            <button type="submit" className="w-full mt-3 rounded-full bg-splash-green text-white py-2.5 text-sm font-semibold" data-testid="admin-gate-submit">Continue</button>
          </form>
        )}
      </div>
    </main>
  );
}

// Shows the sign-in card until authenticated (Google session or backup password), then the children.
export function AdminGate({ title, children }) {
  const location = useLocation();
  const [state, setState] = useState(() => (adminPw ? 'backup' : adminUser || location.state?.adminUser ? 'google' : null));
  const [user, setUser] = useState(adminUser || location.state?.adminUser || null);

  useEffect(() => {
    if (state) return;
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => { setAdminUser(u); setUser(u); setState('google'); })
      .catch(() => setState('signed_out'));
  }, [state]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    setAdminPw(''); setAdminUser(null); setUser(null); setState('signed_out');
  };

  if (!state) return <main className="min-h-screen bg-splash-cream grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-splash-teal" /></main>;
  if (state === 'signed_out') return <SignInCard title={title} onBackup={() => setState('backup')} />;
  return (
    <main className="min-h-screen bg-splash-cream px-4 md:px-8 py-8">
      <div className="max-w-6xl mx-auto">
        <AdminNav user={user} onLogout={logout} />
        {children}
      </div>
    </main>
  );
}
